/**
 * @typedef {import('ol').Feature} Feature
 *
 * @typedef  {{ id: number, [k: string]: any }} SourceModel
 * @typedef {{ positionableType: string, _unsaved: Boolean } & SourceModel} SourceModelUpdate
 * @typedef {{ feature: Feature, sourceModelId: number, targetId: number, targetType: string, positionableType: string, inspectionId: number }} InstanceModel
 *
 * @typedef {{ [tmpId: number]: SourceModelUpdate }} SourceModelPendingUpdates
 * @typedef {{ [featureOlUid: string]: InstanceModel }} InstanceModelPendingUpdates
 *
 * @typedef {{ source: SourceModelPendingUpdates, instance: InstanceModelPendingUpdates }} PendingUpdates
 */

import React, { useState, useReducer, useContext, useEffect } from 'react';
import { flatten } from 'lodash';
import { useQueryClient } from 'react-query';

import { mapUtil, MapContextType } from '@stormwater-software/common';
import positionableApi from '../../../utils/api/positionable';
import { getApi } from '../../../utils/positionable';
import findingApi from '../../../utils/api/finding';
import { useOnBackgroundJobDone } from '../../../hooks/backgroundUploads';
import { jobFindingIds } from '../../../utils/backgroundPhotoUploads';
import { Context as DataSourceContext } from './../DataSourceContext';
import { MapEditorRouteContext } from '../../CustomRoutes/MapEditor';
import { getTargetKey } from './DataSource';
import { pixelToLatLng } from '../../../utils/geo';

const Context = React.createContext();

const defaultPendingUpdateState = () => ({ instance: {}, source: {} });

/** Builds a pixel to latitude/longitude converter or returns null if the map is not calibrated. */
const getGeoForPixel = (olMap, { gisEnabled, gisCalibrated, projectUpload }) => {
  const extent = olMap && olMap.getView().getProjection().getExtent();
  const [imgW, imgH] = extent ? [extent[2], extent[3]] : [null, null];

  if (
    !gisEnabled ||
    !gisCalibrated ||
    projectUpload?.geo_top_left_lat == null ||
    !imgW ||
    !imgH
  ) {
    return null;
  }

  return (px, py) => {
    const [lng, lat] = pixelToLatLng(px, py, imgW, imgH, projectUpload);

    return { latitude: lat, longitude: lng };
  };
};

const configToLatLng = (config, geoForPixel) => {
  const coordinates = config?.coordinates;

  if (!Array.isArray(coordinates) || !coordinates.length) return undefined;

  const toGeo = (node) => {
    if (Array.isArray(node[0])) return node.map(toGeo);
    const { latitude, longitude } = geoForPixel(node[0], node[1]);

    return [longitude, latitude];
  };

  return { ...config, coordinates: toGeo(coordinates) };
};

const geoFields = (config, [px, py], geoForPixel) => {
  if (!geoForPixel) return {};

  const configLatLng = configToLatLng(config, geoForPixel);

  return {
    ...geoForPixel(px, py),
    ...(configLatLng ? { config_latlng: JSON.stringify(configLatLng) } : {}),
  };
};

const saveAPIBody = (dataSource, pendingUpdates, geoForPixel) => {
  const featureIds = Object.keys(pendingUpdates);
  const updatedFeatures = [];

  /**
   * group data by (target type, target id, inspection id)
   *
   * @typedef {{ [tType_tId_iId: string]: Object[] }} GroupedUpdates
   * @type {GroupedUpdates}
   */
  const groupedData = featureIds.reduce((groups, id) => {
    const {
      feature,
      positionableType,
      targetType,
      targetId,
      startInspectionId,
      endInspectionId,
      state,
      inspectionCount
    } = pendingUpdates[id];

    const xy = mapUtil.getFeatureXY(feature);
    const key = [targetId, targetType, startInspectionId].join('--');

    const { positionableContext } =
      dataSource.getDataTypeArguments(positionableType);

    if (!groups[key]) {
      groups[key] = [];
    }
    updatedFeatures.push(feature);

    groups[key].push({
      position_x: xy[0],
      position_y: xy[1],
      id: feature.get('positionables_id'),
      positionable_id: feature.get('positionable_source_id'),
      target_id: targetId,
      start_inspection_id: startInspectionId,
      end_inspection_id: endInspectionId,
      target_type: targetType,
      positionable_type: positionableContext
        ? positionableContext
        : positionableType,
      _context: positionableContext ? positionableType : undefined,
      positionable_config: JSON.stringify(
        feature.get('positionable_config') || {},
      ),
      state: state ? state : false,
      inspectionCount: inspectionCount,
      ...geoFields(feature.get('positionable_config'), xy, geoForPixel),
    });

    return groups;
  }, {});

  return { groupedData, featureIds };
};
const pendingUpdateReducer = (state, action) => {
  switch (action.type) {
    case 'updateInstance':
      return {
        ...state,
        instance: {
          ...state.instance,
          ...action.payload, // : Feature
        },
      };
    case 'updateSource':
      return {
        ...state,
        source: {
          ...state.source,
          ...action.payload, // : Feature
        },
      };
    case 'removeItem':
      const { [action.payload.itemKey]: _, ...newGroupState } =
        state[action.payload.groupKey];

      return {
        ...state,
        [action.payload.groupKey]: newGroupState,
      };
    case 'clear':
      return defaultPendingUpdateState();
    default:
      throw new Error();
  }
};

/**
 * Save pending changes via xhr
 *
 * @async
 * @param {InstanceModelPendingUpdates} pendingUpdates
 */
// const apiSave = (dataSource, pendingUpdates) => {
//   const { featureIds, groupedData } = saveAPIBody(dataSource, pendingUpdates)

//   return { groupedData, featureIds }
// }

/**
 * Save pending changes via xhr
 *
 * @async
 * @param {InstanceModelPendingUpdates} pendingUpdates
 */
const apiSave = (dataSource, pendingUpdates, geoForPixel, versionDate) => {
  const { featureIds, groupedData } = saveAPIBody(
    dataSource,
    pendingUpdates,
    geoForPixel,
  );

  /** xhr for each group */
  const p = Object.keys(groupedData).map((target) => {
    const [targetId, targetType] = target.split('--');

    /**
     * SW2-2346 — the map version this save is made against.
     *
     * Passed only when the user is on an older version. The backend files the change
     * under that version's date instead of stamping today, which is what previously
     * made an add on an older version appear as a brand new version.
     */
    const body = versionDate
      ? groupedData[target].map((item) => ({ ...item, versionDate }))
      : groupedData[target];

    return positionableApi.bulkUpdate(targetType, targetId, body).then(flatten);
  });

  return Promise.all(p)
    .then((data) => {
      // remove updated features from map
      featureIds.forEach((id) => {
        const { feature } = pendingUpdates[id];

        /** remove unsaved features from map */
        if (typeof feature.get('onSaveCallback') === 'function') {
          feature.get('onSaveCallback').call(null);
        }
      });

      return flatten(data);
    })
    .then((models) =>
      models.map((model) => ({
        ...model,
        positionable_type: model._context || model.positionable_type,
      })),
    );
};

/**
 * Save a single source model, via api
 *
 * @todo create vs update, depending on 'id' key
 */
const apiSaveSourceModel = (data, positionableType, methodArguments) => {
  if (data.id) {
    if (positionableType === 'commentables')
      return getApi(positionableType).update(...methodArguments, data);
    else return getApi(positionableType).update(data.projectId, data);
  } else {
    // SW2-2458: a new finding is saved now and its photos upload in the
    // background, so placing it on the map does not wait on the upload. Edits
    // keep waiting: a background closure save returns no finding to place.
    if (positionableType === 'findings') {
      return getApi(positionableType).create(...methodArguments, data, {
        backgroundPhotos: true,
      });
    }

    return getApi(positionableType).create(...methodArguments, data);
  }
};

/**
 * SW2-2346 — versionDate is the map version the removal was made on.
 *
 * It has to be threaded the whole way down: the modal calls this context's delete, which
 * calls here, which calls bulkDelete, which calls the api. Any layer that forgets the
 * argument drops it silently and the removal stamps today instead of the version.
 */
const apiDeletePositionable = (feature, selectedInspId, versionDate) => {
  if (feature.get('positionables_id')) {
    return positionableApi
      .bulkDelete([feature.get('positionables_id')], selectedInspId, versionDate)
      .then(() => feature);
  }

  return Promise.resolve(feature);
};

/**
 * @param {number} sourceModelId
 * @return {Promise<number>}
 */
const apiDeleteSourceModel = (
  sourceModelId,
  positionableType,
  methodArguments,
) => {
  return getApi(positionableType)
    .destroy(sourceModelId, ...methodArguments)
    .then(() => sourceModelId);
};

const updateInstanceModelDataContext = (instanceModels, updateItem) => {
  instanceModels.forEach((model) => {
    const { positionable_type, target_type, target_id, start_inspection_id } =
      model;

    updateItem(
      getTargetKey(
        positionable_type,
        target_type,
        target_id,
        start_inspection_id,
      ),
      model,
    );
  });

  return instanceModels;
};

/**
 * @param {InstanceModelPendingUpdates} pendingUpdates
 */
const discardInstanceModelUpdates = (pendingUpdates, getLayer) => {
  Object.keys(pendingUpdates)
    .filter((k) => pendingUpdates[k] !== undefined)
    .map((k) => pendingUpdates[k].feature)
    .forEach((feature) => {
      if (
        feature.get('positionables_id') &&
        typeof feature.get('handleRevert') === 'function'
      ) {
        feature.get('handleRevert').call(null, []);
      } else {
        getLayer(feature).getSource().removeFeature(feature);
      }
    });
};

const discardSourceModelUpdates = (pendingUpdates, removeItem) => {
  Object.keys(pendingUpdates)
    .map((k) => pendingUpdates[k])
    .forEach(({ positionableType, id }) => {
      removeItem(positionableType, id);
    });
};

/**
 * SW2-2458. Give the layers already on the map for `sourceModel` its current
 * style, in place.
 *
 * mapContext.reloadLayer cannot do this: it looks layers up as
 * `type--layerName`, but @stormwater-software/common now names them
 * `type--layerName--positionableId`, so it never finds one and does nothing.
 * Build the style the way the library would for a new layer, then copy it onto
 * every layer holding this source model's features. The features never move.
 */
const restyleSourceModelLayers = (mapContext, type, sourceModel) => {
  try {
    const { map } = mapContext;
    const styled = mapContext.getUtil(type).getLayer(sourceModel);

    if (!map || !styled) return;

    map
      .getLayers()
      .getArray()
      .forEach((layer) => {
        const source = layer.getSource && layer.getSource();
        const features = source && source.getFeatures ? source.getFeatures() : [];
        const holdsModel = features.some(
          (feature) =>
            Number(feature.get('positionable_source_id')) ===
              Number(sourceModel.id) &&
            [type, sourceModel.positionableType].includes(
              feature.get('positionable_type'),
            ),
        );

        if (holdsModel) layer.setStyle(styled.getStyle());
      });
  } catch {
    // At worst this source model keeps its old style until the next load.
  }
};

/**
 * PositionableInstanceDataContext
 *
 * Provides context to manage positionable instance data
 *
 * Implements update/delete for positionable instances via XHR
 * Provides API to report data changes (used by map interactions)
 * Provides API to save or discard changes
 *
 * @odo @maybe Provides API to track "selection" of positionable instances
 * @odo @maybe Provides API to delete selected positionable instances
 */
function PositionableInstanceDataContext(props) {
  const dataSource = useContext(DataSourceContext);
  const mapContext = useContext(MapContextType);
  const queryClient = useQueryClient();
  const { projectId, projectUploadId } = useContext(MapEditorRouteContext);
  const routeContext = useContext(MapEditorRouteContext);
  const [arr, setArr] = useState([]);

  const [hasPendingUpdates, setHasPendingUpdates] = useState(false);

  /** @type {[PendingUpdates, Function]} */
  const [pendingUpdates, dispatch] = useReducer(
    pendingUpdateReducer,
    defaultPendingUpdateState(),
  );

  useEffect(() => {
    setHasPendingUpdates(
      Object.keys(pendingUpdates.instance).length >= 1 ||
        Object.keys(pendingUpdates.source).length >= 1,
    );
  }, [pendingUpdates]);

  // SW2-2458: a finding's photos finished uploading in the background. The map
  // keeps its own copy of each finding, so refresh that one finding in place
  // (no page reload); its popup and edit form read from this copy.
  useOnBackgroundJobDone({ kind: 'finding' }, (job) => {
    jobFindingIds(job).forEach((id) => {
      if (!dataSource.getById(id, 'findings')) return;

      findingApi
        .show(id)
        .then((fresh) => {
          if (!fresh) return;

          dataSource.patchArrayItem('findings', id, {
            finding_uploads: fresh.finding_uploads || [],
            date_completed: fresh.date_completed,
          });
        })
        .catch(() => {});
    });
  });

  const contextValue = {
    hasPendingUpdates,
    /**
     * Add or update an instance model
     */
    setPendingUpdate: (
      feature,
      positionableType,
      targetType,
      targetId,
      startInspectionId,
      endInspectionId,
      state,
      inspectionCount
    ) => {
      dispatch({
        type: 'updateInstance',
        payload: {
          [feature.ol_uid]: {
            feature,
            positionableType,
            targetType,
            targetId,
            startInspectionId,
            endInspectionId,
            sourceModelId: feature.get('positionable_source_id'),
            state,
            inspectionCount
          },
        },
      });
    },

    /**
     * Save a source model
     */
    createSourceModel: (sourceModelFormData, positionableType) => {
      const { positionableIndexArguments } =
        dataSource.getDataTypeArguments(positionableType);

      let args = positionableIndexArguments || [];

      if (typeof args === 'function') {
        args = args(sourceModelFormData);
      }
      sourceModelFormData = {
        ...sourceModelFormData,
        projectId: args[0],
        divisionId: args[1],
      };

      return apiSaveSourceModel(sourceModelFormData, positionableType, args)
        .then((data) => ({
          positionableType,
          ...data,
        }))
        .then((data) => {
          dataSource.updateArrayItem(positionableType, data);

          return data;
        });
    },

    save: () => {
      return apiSave(
        dataSource,
        pendingUpdates.instance,
        getGeoForPixel(mapContext.map, routeContext || {}),
        routeContext?.isLatestMapVersion === false
          ? routeContext?.selectedVersionDate
          : null,
      )
        .then((instanceModels) => {
          /** @todo update source models */
          dispatch({
            type: 'clear',
          });

          updateInstanceModelDataContext(
            instanceModels,
            dataSource.updateArrayItem,
          );

          arr.length &&
            arr.forEach((feature) => {
              const layer = mapContext.getLayer(
                feature.get('positionable_type'),
                dataSource.getById(
                  feature.get('positionable_source_id'),
                  feature.get('positionable_type'),
                ),
              );

              layer.getSource().removeFeature(feature);
            });

          setArr([]);
          return instanceModels;
        })
        .then((instanceModels) => {
          const versionsKey = [
            'project',
            'map-versions',
            projectId,
            projectUploadId,
          ];

          /**
           * SW2-2345 — refetch the versions after every save, not only when today is
           * missing from them.
           *
           * The guard here asked whether TODAY was already in the list and skipped the
           * refetch if it was. A save does not necessarily mint today's version: changing
           * a legend item's Added On date files it against the day the user chose, which
           * is the whole point of the date — so the new version could be any day, and the
           * question the guard asked had no bearing on it. With today already present,
           * which it usually is once anyone has drawn, the refetch never ran and the new
           * version simply did not appear in the picker until the page was reloaded.
           *
           * (It also compared a UTC `toISOString` day against version days derived in the
           * session timezone, so it could answer wrongly either side of midnight.)
           *
           * Unconditional now. It is one small request against a list the user is about
           * to read, and the alternative is showing them a version list that does not
           * contain the version they just created.
           */
          queryClient.invalidateQueries(versionsKey);

          // SW2-2458: this reloaded the whole page whenever an existing item was
          // saved (it was meant for legend-item edits, whose layer kept stale
          // params). That threw away any photos still uploading in the
          // background and reloaded the full map on a weak signal after every
          // marker move. Rebuild just the affected layers from the saved data
          // instead, from freshly fetched source models (a style edited since
          // the map loaded lives there); the features stay where they were dropped.
          // Pick up rows the save replaced (see PositionableDataSource). The
          // save resolves only once they have arrived: until then a marker
          // could still carry a retired id, and saving it again would fork it.
          //
          // If the refresh fails the map may hold retired ids, so it falls back
          // to the full reload this replaced, and the save never resolves: the
          // toolbar stays busy until the page is gone. (With photos still
          // uploading the browser asks first; staying keeps the map busy.)
          const refreshed = instanceModels.some((model) => model.isNew === false)
            ? dataSource.refreshInstances().catch(() => {
                window.location.reload();

                return new Promise(() => {});
              })
            : undefined;

          const types = Array.from(
            new Set(
              instanceModels
                .filter((model) => model.isNew === false)
                .map((model) => model._context || model.positionable_type),
            ),
          );

          types.forEach((type) => {
            const typeArgs = dataSource.getDataTypeArguments(type);

            if (!typeArgs) return;

            const indexArgs =
              typeof typeArgs.positionableIndexArguments === 'function'
                ? typeArgs.positionableIndexArguments()
                : typeArgs.positionableIndexArguments || [];

            getApi(type)
              .index(...indexArgs)
              .then((models) => {
                const fresh = models.map((model) => ({
                  ...model,
                  positionableType: type,
                }));

                // The same as the map's own load (PositionableDataSource).
                dataSource.addDataType(type, fresh);
                fresh.forEach((sourceModel) =>
                  restyleSourceModelLayers(mapContext, type, sourceModel),
                );
              })
              .catch(() => {
                // Leave the map as it is; the save itself succeeded.
              });
          });

          return refreshed;
        })
        .catch((err) => {
          console.error('save', err, pendingUpdates);
          /** @todo send some updates back to the queue */
          // if (this.props.onError) {
          //   this.props.onError("There was an error saving. Please try again.");
          // }
        });
    },
    /**
     * Discard pending updates
     *
     * remove & detach new/unsaved features
     * revert changes to existing features via callback function
     */
    discard: () => {
      discardInstanceModelUpdates(pendingUpdates.instance, (feature) =>
        mapContext.getLayer(
          feature.get('positionable_type'),
          dataSource.getById(
            feature.get('positionable_source_id'),
            feature.get('positionable_type'),
          ),
        ),
      );

      discardSourceModelUpdates(
        pendingUpdates.source,
        dataSource.removeArrayItem,
      );

      dispatch({
        type: 'clear',
      });
    },

    undo: () => {
      const pendingUndo = [...dataSource.undo];
      const data = pendingUndo.pop();
      
      dataSource.setUndo(pendingUndo);
      const {
        feature,
        positionableType,
        targetType,
        targetId,
        startInspectionId,
        state
      } = data;

      setArr([...arr, feature]);

      dispatch({
        type: 'updateInstance',
        payload: {
          [feature.ol_uid]: {
            feature,
            positionableType,
            targetType,
            targetId,
            sourceModelId: feature.get('positionable_source_id'),
            startInspectionId: startInspectionId,
            state
          },
        },
      });

      const layer = mapContext.getLayer(
        feature.get('positionable_type'),
        dataSource.getById(
          feature.get('positionable_source_id'),
          feature.get('positionable_type'),
        ),
      );

      layer.getSource().addFeature(feature);
    },

    /**
     * Delete feature
     *
     */
    delete: (feature, selectedInspId, versionDate) =>
      apiDeletePositionable(feature, selectedInspId, versionDate).then(() => {
        if (pendingUpdates.instance.hasOwnProperty(feature.ol_uid)) {
          dispatch({
            type: 'removeItem',
            payload: {
              groupKey: 'instance',
              itemKey: feature.ol_uid,
            },
          });
        }

        const positionableType = feature.get('positionable_type');

        const { targetType, targetId, startInspectionId } =
          dataSource.getDataTypeArguments(positionableType);

        if (feature.get('positionables_id')) {
          dataSource.setUndo([
            ...dataSource.undo,
            {
              feature,
              positionableType,
              targetType,
              targetId,
              startInspectionId,
              state:'undo'
            },
          ]);

          dataSource.removeArrayItem(
            [positionableType, targetType, targetId, startInspectionId].join(
              '--',
            ),
            feature.get('positionables_id'),
          );
        } else {
          const layer = mapContext.getLayer(
            feature.get('positionable_type'),
            dataSource.getById(
              feature.get('positionable_source_id'),
              feature.get('positionable_type'),
            ),
          );

          dataSource.setUndo([
            ...dataSource.undo,
            {
              feature,
              positionableType,
              targetType,
              targetId,
              startInspectionId,
            },
          ]);

          layer.getSource().removeFeature(feature);
        }

        return feature;
      }),

    deleteSourceModel: (positionableType, sourceModel) => {
      const { positionableIndexArguments } =
        dataSource.getDataTypeArguments(positionableType);

      let args = positionableIndexArguments || {};

      if (typeof args === 'function') {
        args = args(sourceModel);
      }

      return apiDeleteSourceModel(sourceModel.id, positionableType, args).then(
        () => {
          dataSource.removeArrayItem(positionableType, sourceModel.id);
        },
      );
    },

    countInstances: (
      positionableType,
      targetType,
      targetId,
      sourceModel,
      startInspectionId,
    ) => {
      const pending = Object.keys(pendingUpdates.instance)
        .map((id) => pendingUpdates.instance[id])
        .filter(
          (data) =>
            !data.feature.get('positionables_id') &&
            data.sourceModelId === sourceModel.id &&
            data.positionableType === positionableType &&
            data.targetType === targetType &&
            data.targetId === targetId &&
            data.inspectionId === startInspectionId,
        ).length;

      const saved = dataSource.filter(
        getTargetKey(positionableType, targetType, targetId, startInspectionId),
        {
          positionable_type: positionableType,
          positionable_id: sourceModel.id,
          target_type: targetType,
          target_id: targetId,
          inspection_id: startInspectionId,
        },
      ).length;

      return pending + saved;
    },

    /** dev only! */
    pendingUpdates: pendingUpdates,
  };

  return (
    <Context.Provider value={contextValue}>{props.children}</Context.Provider>
  );
}

export { Context };
export default PositionableInstanceDataContext;
